import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Waves, Loader2 } from "lucide-react";

const STATS = [
  { value: "71", label: "Chambres SIP" },
  { value: "2", label: "Serveurs Asterisk" },
  { value: "3", label: "Hôtels actifs" },
  { value: "99.9%", label: "Uptime SIP" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — 45% — brand identity */}
      <div
        className="hidden sm:flex flex-col justify-between border-r border-zinc-800 px-10 py-10"
        style={{ width: "45%", backgroundColor: "#0d1117" }}
      >
        {/* Logo + tagline */}
        <div>
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-blue-600">
              <Waves className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-head text-lg font-black tracking-tighter text-white">
              HotelSIP<span className="text-blue-600">.</span>
            </span>
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            Provisioning SIP par chambre · Asterisk multi-serveur
          </p>
        </div>

        {/* Mini-stats grid 2×2 */}
        <div className="grid grid-cols-2 gap-3">
          {STATS.map(({ value, label }) => (
            <div
              key={label}
              className="rounded-sm border border-zinc-800 px-4 py-3"
              style={{ backgroundColor: "#161b22" }}
            >
              <div className="font-mono text-xl font-black text-white">{value}</div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-600 mt-0.5">
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel — 55% — login form */}
      <div className="flex flex-col justify-center flex-1 bg-white px-8 py-12 sm:px-16">
        <div className="mx-auto w-full max-w-xs">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">
            Connexion
          </p>
          <h1 className="mt-2 font-head text-2xl font-black tracking-tighter text-zinc-900">
            Console d'administration
          </h1>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                Email
              </Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 rounded-sm font-mono text-sm"
                required
              />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                Mot de passe
              </Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 rounded-sm font-mono text-sm"
                required
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {error}
              </div>
            )}

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-sm bg-blue-600 font-semibold hover:bg-blue-700"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Se connecter"}
            </Button>

          </form>
        </div>
      </div>
    </div>
  );
}
